import { PlusOutlined } from '@ant-design/icons';
import { Form, type FormItemProps, Input, InputNumber } from 'antd';

import {
  AddressSearchSelect,
  BvSearchSelect,
  CampaignSearchSelect,
  CvExecutionSearchSelect,
  ProductSearchSelect,
} from '@/components/form-fields';
import { getFormItemLabel } from '@/components/form-fields/utils';
import type { Ii18nLabel } from '@/types/common';
import { AddressType, ProductionStatus, ProductQuality } from '@/types/enums';
import { buildDropdownOptionsFromEnum } from '@/utils/common';
import { getFormattedVolume } from '@/utils/formatters';
import { getParsedVolume } from '@/utils/parsers';
import { getTranslatedString } from '@/utils';

const Numbers = ({ label, i18nLabel }: FormItemProps & Ii18nLabel) => {
  const fieldLabel = getFormItemLabel(i18nLabel, label);
  return (
    <Form.Item label={fieldLabel}>
      <Input.Group compact={true}>
        <div className="lot-numbers">
          <Form.Item name="number" noStyle={true} rules={[{ required: true }]}>
            <InputNumber className="lot-numbers-number" min={1} />
          </Form.Item>
          <PlusOutlined className="lot-numbers-plus" />
          <Form.Item name="count" noStyle={true} rules={[{ required: true }]}>
            <InputNumber className="lot-numbers-count" min={0} max={50} placeholder="Count" />
          </Form.Item>
        </div>
      </Input.Group>
    </Form.Item>
  );
};

export const getFormFields = (onChangeLocation: any, includeCount = false) => [
  {
    name: 'number',
    params: {
      i18nLabel: 'lots.number',
      type: 'number',
    },
    conditionalRender: () => includeCount,
    component: Numbers,
  },
  {
    name: 'executionCvId',
    component: CvExecutionSearchSelect,
    params: {
      i18nLabel: 'lots.execution-cv',
    },
    rules: [{ required: !true }],
  },
  {
    name: 'bvId',
    component: BvSearchSelect,
    params: {
      i18nLabel: 'lots.bv',
    },
    rules: [{ required: !true }],
  },
  {
    name: 'productionDate',
    componentName: 'datepicker',
    params: {
      i18nLabel: 'lots.production-date',
      showTime: true,
    },
    rules: [{ required: true }],
  },
  {
    name: 'factoryId',
    component: AddressSearchSelect,
    params: {
      i18nLabel: 'lots.factory',
      type: AddressType.Factory,
      onChange: onChangeLocation,
    },
    rules: [{ required: true }],
  },
  {
    name: 'locationId',
    component: AddressSearchSelect,
    params: {
      i18nLabel: 'lots.location',
    },
    rules: [{ required: true }],
  },
  {
    name: 'productId',
    component: ProductSearchSelect,
    params: {
      i18nLabel: 'lots.product',
    },
    rules: [{ required: true }],
  },
  {
    name: 'quality',
    componentName: 'dropdown',
    params: {
      i18nLabel: 'lots.quality',
      options: buildDropdownOptionsFromEnum(ProductQuality),
    },
    rules: [{ required: true }],
  },
  {
    name: 'sacksQuantity',
    componentName: 'input-number',
    params: {
      i18nLabel: 'lots.sacks-quantity',
      formatter: getFormattedVolume,
      parser: getParsedVolume,
    },
    rules: [{ required: true }],
  },
  {
    name: 'theoreticalWeight',
    componentName: 'input-number',
    params: {
      i18nLabel: 'lots.theoretical-weight',
    },
  },
  {
    name: 'weight',
    componentName: 'input-number',
    params: {
      i18nLabel: 'lots.weight',
    },
    rules: [{ required: true }],
  },
  {
    name: 'campaignId',
    component: CampaignSearchSelect,
    params: {
      i18nLabel: 'lots.campaign',
    },
    rules: [{ required: true }],
  },
  {
    name: 'productionStatus',
    componentName: 'dropdown',
    params: {
      i18nLabel: 'lots.production-status',
      options: buildDropdownOptionsFromEnum(ProductionStatus).map(({ label, value }) => ({
        value,
        label: getTranslatedString(`lots.production-status.${label}`),
      })),
    },
    rules: [{ required: true }],
  },
];
